import { recentDisplayTurnaround } from "../../services/tripDisplayDuration";
import React, { useEffect, useState } from "react";
import { useGetDockRecentOperationsQuery } from "../../api/vmsSlice";
import { useDockChangeRefetch, useTabVisible, useSecondsTick, DOCK_FALLBACK_POLL_MS } from "./useDockRefresh";
import { lifecycleBadgeClass, lifecycleBadgeLabel, lifecycleSummary } from "./dockLifecycle";

const RECENT_PAGESIZE = 20;
// The same two ranges the peak-activity panel offers, so both read the plant's calendar the same way.
// "Today" is plant-local midnight to now, never a rolling 24 hours.
const RECENT_RANGES = [
	{ key: "today", label: "Today" },
	{ key: "week", label: "Last 7 days" },
];
// Fallbacks only; the live values arrive in the response's `thresholds` from plantConfig.
const FALLBACK_SLA_MINUTES = 90;
const FALLBACK_DOOR_MINUTES = 45;

// Formats a timestamp in the plant timezone the response supplies.
function formatStarted(value, timezone) {
	if (!value) return "—";
	return new Intl.DateTimeFormat("en-MY", {
		timeZone: timezone, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
	}).format(new Date(value));
}

// Formats an elapsed span. Durations are spans, not instants, so no timezone is involved.
function formatSpan(milliseconds) {
	if (!Number.isFinite(milliseconds)) return "—";
	const minutes = Math.max(0, Math.round(milliseconds / 60000));
	if (minutes < 60) return `${minutes} min`;
	return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}

// Converts a milestone name to a readable operation label.
function operationLabel(operation) {
	if (operation === "UNLOADING") return "Unload";
	if (operation === "LOADING") return "Load";
	return "—";
}

/**
 * One dock operation. A row is one Bay visit, so a chartered trip that used two Bays appears twice,
 * sharing one turnaround.
 *
 * Running spans are computed here from the row's anchor timestamps against the local clock, because
 * the backend deliberately sends null for them: a server-side snapshot would freeze between polls and
 * read as a stopped watch.
 */
function OperationRow({ row, timezone, thresholds, onViewTimeline }) {
	const cycleMs = row.live && row.bayInAt ? Date.now() - new Date(row.bayInAt).getTime() : row.cycleMs;
	const doorOpenMs = row.doorOpenSince
		? row.doorOpenMs + (Date.now() - new Date(row.doorOpenSince).getTime())
		: row.doorOpenMs;
	const overSla = Number.isFinite(cycleMs) && cycleMs / 60000 > thresholds.slaMinutes;
	const doorTooLong = Boolean(row.doorOpenSince) && doorOpenMs / 60000 > thresholds.doorOpenLongMinutes;
	const divertedFrom = row.allocatedBay && row.allocatedBay !== row.dock ? row.allocatedBay : null;
	// Spells out which instants the turnaround spans, so the number can be checked rather than trusted.
	const turnaroundTitle = [
		row.queueInAt && `Queued ${formatStarted(row.queueInAt, timezone)}`,
		row.gateInHidden ? "Gate In not detected" : row.gateInAt && `Gate in ${formatStarted(row.gateInAt, timezone)}`,
		row.gateInHidden && !row.queueInAt && row.firstBayInAt && `Duration from first Bay ${formatStarted(row.firstBayInAt, timezone)}`,
		row.gateOutAt && `Gate out ${formatStarted(row.gateOutAt, timezone)}`,
	].filter(Boolean).join(" → ") || undefined;

	/**
	 * Status reports where the operation actually is, not whether it beat a target.
	 *
	 * The SLA-breach verdict is parked: plantConfig.dockSlaMinutes is still unset for this plant, so
	 * every "breach" would be judged against a placeholder 90 minutes that nobody has agreed. To
	 * restore it, uncomment the two branches marked SLA below.
	 *
	 * "On track" is not a synonym for live — it is the third real case: a Bay visit that finished on a
	 * trip that has not reached Gate OUT yet, so the operation is neither running nor complete.
	 */
	let statusLabel = "On track";
	let statusClass = "dock-status-vacant";
	// if (row.live && overSla) {              // SLA
	// 	statusLabel = "SLA breach";           // SLA
	// 	statusClass = "dock-status-occupied";  // SLA
	// } else
	if (row.live && doorTooLong) {
		statusLabel = "Door open";
		statusClass = "dock-status-reserved";
	} else if (row.live) {
		statusLabel = "Ongoing";
		statusClass = "dock-status-ongoing";
	} else {
		const tripStatus = String(row.tripStatus || "").toLowerCase();
		if (tripStatus === "terminated" || tripStatus === "cancelled") {
			statusLabel = row.tripStatus;
			statusClass = "dock-status-terminated";
		// } else if (overSla) {                    // SLA
		// 	statusLabel = "SLA breach";            // SLA
		// 	statusClass = "dock-status-occupied";  // SLA
		} else if (tripStatus === "completed") {
			statusLabel = "Completed";
			statusClass = "dock-status-completed";
		}
	}

	/**
	 * The provisional badge sits beside the operation's own status rather than replacing it: this row
	 * is a Bay visit that really did finish, and the lifecycle is a statement about the trip it
	 * belongs to. Overwriting the row status would lose the distinction the operator needs.
	 */
	const lifecycle = row.dockLifecycle;
	const lifecycleLabel = lifecycleBadgeLabel(lifecycle);

	const handleKeyDown = (event) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			onViewTimeline(row.tripId);
		}
	};

	return (
		<tr
			className={row.live ? "dock-ops-row dock-ops-row-live" : "dock-ops-row"}
			role="button"
			tabIndex={0}
			onClick={() => onViewTimeline(row.tripId)}
			onKeyDown={handleKeyDown}
		>
			<td>
				<strong>{row.dock}</strong>
				{divertedFrom && (
					<span className="dock-ops-note" title={`Allocated ${divertedFrom}, actually used ${row.dock}`}>
						allocated {divertedFrom}
					</span>
				)}
			</td>
			<td>
				{row.vehicleNumber || "—"}
				{row.vehicleType && <span className="dock-ops-note">{row.vehicleType}</span>}
			</td>
			{/* <td>{operationLabel(row.operation)}</td> */}
			{/* Started and Ended are the Bay visit, so Ended - Started is the Cycle beside them. The
			    trip's own Gate span is on the tooltip rather than in two more columns. */}
			{/* A visit can be in this table on its departure rather than its arrival — one that arrived
			    before the window and left inside it. Marked, because a start date outside the window
			    otherwise reads as a bug rather than as a vehicle that stayed overnight. */}
			<td
				className="dock-ops-num"
				title={[
					row.gateInHidden ? "Gate In not detected" : row.gateInAt ? `Gate in ${formatStarted(row.gateInAt, timezone)}` : null,
					row.arrivedBeforeWindow ? "Arrived before this window and left inside it" : null,
				].filter(Boolean).join(" · ") || undefined}
			>
				{formatStarted(row.bayInAt, timezone)}
				{row.arrivedBeforeWindow && <em className="dock-ops-soft"> · earlier</em>}
			</td>
			{/* A terminated visit also has no departure, but it is not in progress — the trip was ended
			    while the vehicle was still on the Bay, so it must not read as one still running. */}
			<td className="dock-ops-num" title={row.gateOutAt ? `Gate out ${formatStarted(row.gateOutAt, timezone)}` : undefined}>
				{row.bayOutAt
					? formatStarted(row.bayOutAt, timezone)
					: <em className="dock-ops-soft">{row.live ? "in progress" : "not recorded"}</em>}
			</td>
			<td className={overSla ? "dock-ops-num dock-ops-breach" : "dock-ops-num"}>{formatSpan(cycleMs)}</td>
			<td className={doorTooLong ? "dock-ops-num dock-ops-warn" : "dock-ops-num"}>
				{row.doorOpenCount ? formatSpan(doorOpenMs) : "—"}
				{/* An inferred figure must never be presented as a measured one. */}
				{/* {row.doorClosedByDeparture && (
					<sup className="dock-ops-flag" title="Door close was not reported; measured to Bay departure">~</sup>
				)}
				{row.doorAnomalies > 0 && (
					<sup className="dock-ops-flag" title={`${row.doorAnomalies} door close event(s) had no matching open`}>!</sup>
				)} */}
			</td>
			{/* Turnaround is time on site, so it starts at the queue arrival when the vehicle queued.
			    The queue share is called out rather than folded in silently: a short dock visit behind a
			    long wait is a yard problem, and one combined figure cannot show that. */}
			<td className="dock-ops-num" title={turnaroundTitle}>
				{row.live ? <em>live</em> : formatSpan(recentDisplayTurnaround(row))}
				{row.queueMs > 0 && <span className="dock-ops-note">incl. {formatSpan(row.queueMs)} queue</span>}
			</td>
			{/* <td className="dock-ops-num">
				{row.unitsUnloadedTotal || row.unitsLoadedTotal || "—"}
			</td> */}
			<td>
				{/* <span className={`dock-status ${statusClass}`}>{statusLabel}</span>
				{lifecycleLabel && <span className={`dock-status dock-status-lifecycle ${lifecycleBadgeClass(lifecycle)}`} title={lifecycleSummary(lifecycle, timezone)}>{lifecycleLabel}</span>} */}
				<span className={`dock-status ${statusClass}`}>{row.tripStatus || "—"}</span>
			</td>
		</tr>
	);
}

/**
 * Recent and live dock operations with cycle, door-open and turnaround times.
 *
 * Unlike the peak-activity chart, this panel IS wired to dock-operations-changed: a door opening
 * changes the Status column immediately, which is the point of the panel. It shares the board's
 * 30-second fallback poll and its tab-visibility pause, so a truck arriving still costs one debounced
 * refetch rather than four.
 */
export default function RecentOperations({ plantId, canView, onViewTimeline }) {
	const [pageNo, setPageNo] = useState(1);
	const [range, setRange] = useState("today");
	// null means every Bay, and the key is omitted so that request stays identical to the default one.
	const [selectedBay, setSelectedBay] = useState(null);
	const isTabVisible = useTabVisible();
	const query = useGetDockRecentOperationsQuery(
		{
			plantId, range, pagesize: RECENT_PAGESIZE, page_no: pageNo,
			...(selectedBay ? { checkpointNumbers: [selectedBay] } : {}),
		},
		{ skip: !plantId || !canView, pollingInterval: isTabVisible ? DOCK_FALLBACK_POLL_MS : 0 },
	);
	useDockChangeRefetch(plantId, query.refetch);

	const response = query.data;
	const data = response?.data;
	const rows = data?.rows || [];
	const bays = data?.bays || [];
	const count = Number(response?.count) || 0;
	const pageCount = Math.max(1, Math.ceil(count / RECENT_PAGESIZE));
	// Only tick while something is actually running; a page of finished operations has nothing that moves.
	useSecondsTick(rows.some((row) => row.live || row.doorOpenSince));

	// Plant configuration can change under a long-open tab, and a chip the backend would now reject
	// must not stay pressed.
	useEffect(() => {
		if (selectedBay && bays.length && !bays.some((bay) => bay.checkpointNumber === selectedBay)) {
			setSelectedBay(null);
		}
	}, [bays, selectedBay]);
	// A filter or range change invalidates the current page number.
	useEffect(() => setPageNo(1), [selectedBay, range]);
	// A page that no longer exists (operations aged out of the window) would render blank.
	useEffect(() => {
		if (pageNo > pageCount) setPageNo(pageCount);
	}, [pageNo, pageCount]);

	if (!canView) return null;

	const thresholds = {
		slaMinutes: Number(data?.thresholds?.slaMinutes) || FALLBACK_SLA_MINUTES,
		doorOpenLongMinutes: Number(data?.thresholds?.doorOpenLongMinutes) || FALLBACK_DOOR_MINUTES,
	};
	const selectedBayName = data?.selectedBays?.length === 1 ? data.selectedBays[0].displayName : null;
	const scopeLabel = selectedBayName || "All bays";
	const rangeLabel = data?.range === "week" ? "the last 7 days" : "today";
	// Three distinct empty states, so an operator can tell an idle dock from an idle plant.
	const emptyMessage = selectedBayName
		? `No operations recorded at ${selectedBayName} ${rangeLabel}.`
		: `No dock operations recorded ${rangeLabel}.`;

	return (
		<section className="dock-peak-panel dock-ops-panel" aria-label="Recent and live operations">
			<div className="dock-peak-head">
				<div>
					<h2>Recent &amp; live operations</h2>
					<span>
						Cycle times &amp; door-open duration · {scopeLabel}
						{/* The plant's own date, from the backend, so the label cannot claim a different
						    day from the one the rows were selected for. */}
						{data?.fromDate ? ` · ${data.range === "week" ? `${data.fromDate} to ${data.toDate}` : data.toDate}` : ""}
						{response?.timezone ? ` · ${response.timezone.replace("_", " ")}` : ""}
					</span>
				</div>
				<div className="dock-filter-chips" role="group" aria-label="Operations range">
					{RECENT_RANGES.map((option) => (
						<button
							key={option.key}
							type="button"
							className="dock-chip"
							aria-pressed={range === option.key}
							onClick={() => setRange(option.key)}
						>
							{option.label}
						</button>
					))}
				</div>
				{data?.summary && (
					<div className="dock-ops-summary">
						<span><strong>{data.summary.liveCount}</strong> live</span>
						<span><strong>{data.summary.completedCount}</strong> finished</span>
						{/* Median, not average: one wedged trip would skew a mean over a handful of rows. */}
						{data.summary.medianCycleMs != null && <span>median cycle <strong>{formatSpan(data.summary.medianCycleMs)}</strong></span>}
						{data.summary.medianDoorOpenMs != null && <span>median door <strong>{formatSpan(data.summary.medianDoorOpenMs)}</strong></span>}
					</div>
				)}
			</div>

			{/* Rendered from the response, so the row can never offer a Bay the backend would reject. */}
			{bays.length > 1 && (
				<div className="dock-filter-chips dock-peak-bays" role="group" aria-label="Filter by Bay">
					<button type="button" className="dock-chip" aria-pressed={selectedBay === null} onClick={() => setSelectedBay(null)}>
						All bays
					</button>
					{bays.map((bay) => (
						<button
							key={bay.checkpointNumber}
							type="button"
							className="dock-chip"
							aria-pressed={selectedBay === bay.checkpointNumber}
							onClick={() => setSelectedBay(bay.checkpointNumber)}
						>
							{bay.displayName}
						</button>
					))}
				</div>
			)}

			{(query.error || response?.error) && (
				<div className="dock-message error">{response?.message || "Recent operations could not be loaded."}</div>
			)}

			{query.isLoading ? (
				<div className="dock-peak-placeholder">Loading dock operations…</div>
			) : rows.length ? (
				<>
					<div className="dock-ops-table-wrap">
						<table className="dock-ops-table">
							<thead>
								<tr>
									<th>Dock</th><th>Vehicle</th>{/* <th>Type</th> */}<th>Started</th><th>Ended</th>
									<th>Cycle</th><th>Door open</th><th>Turnaround</th>{/* <th>Units</th> */}<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((row) => (
									<OperationRow
										key={row.rowId}
										row={row}
										timezone={response.timezone}
										thresholds={thresholds}
										onViewTimeline={onViewTimeline}
									/>
								))}
							</tbody>
						</table>
					</div>
					<div className="dock-ops-footer">
						<span>
							Showing {rows.length} of {count} Bay visits
							{data?.capped && " · window truncated at the most recent 200 trips"}
						</span>
						{pageCount > 1 && (
							<div className="dock-ops-pager">
								<button type="button" className="dock-chip" disabled={pageNo <= 1} onClick={() => setPageNo(pageNo - 1)}>
									Previous
								</button>
								<span>Page {pageNo} of {pageCount}</span>
								<button type="button" className="dock-chip" disabled={pageNo >= pageCount} onClick={() => setPageNo(pageNo + 1)}>
									Next
								</button>
							</div>
						)}
					</div>
				</>
			) : (
				!query.error && !response?.error && <div className="dock-peak-placeholder">{emptyMessage}</div>
			)}
		</section>
	);
}
